import { endpoints } from '@/shared/const/endpoints.const';
import { useToastNotif } from '@/shared/hooks';
import { cn } from '@/shared/lib/tailwindMerge';
import { useBoundStore } from '@/shared/store';
import {
  GetRequestResponse,
  PostRequestResponse,
  UploadFileResponse,
} from '@/shared/types/requests.types';
import IconDownloadFile from '@/shared/assets/icons/icon-file-clip.svg?react';
import axios from 'axios';
import Upload from 'rc-upload';
import { RcFile } from 'rc-upload/lib/interface';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';
import { envConfig } from '@/config/env/env.config';

//TODO: this component should be fully re-usable

type Props = {
  setUploadProgress?: (progress: number | null) => void;
  cancelSending?: boolean;
  setCancelSending?: (isCancelling: boolean) => void;
  classNames?: string;
  callbackOnSuccess?: (file: GetRequestResponse<UploadFileResponse>) => void;
  onFileSelect?: (file: RcFile) => void;
  text?: string;
  accept?: string;
  children?: ({
    fileName,
    fileSize,
    setFile,
    setFileSize,
  }: {
    fileName?: string;
    fileSize: string | null;
    setFile: (file: File | null) => void;
    setFileSize: (fileSize: string | null) => void;
  }) => ReactNode;
  meta?: {
    type: string;
    applicationId: string;
    companyTin: string;
  };
  manualUpload?: boolean; //do not automatically upload to server, just set file and fileSize to handle uploading by RHF form
};

const { CancelToken } = axios;
let cancelAx: (text: string) => void;

export function FileUploader(props: Props) {
  const {
    setUploadProgress,
    cancelSending = false,
    setCancelSending,
    classNames = '',
    callbackOnSuccess,
    text = 'Загрузить',
    children,
    meta,
    manualUpload = false,
    onFileSelect,
    accept = '.jpg, .jpeg, .png, .pdf',
  } = props;

  const [progress, setProgress] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileSize, setFileSize] = useState<string | null>(null);

  const [t] = useTranslation();

  const { accessToken } = useBoundStore(
    useShallow((state) => ({
      accessToken: state.accessToken,
      lng: state.lng,
    })),
  );

  const { showToast } = useToastNotif();
  const fileName = file?.name;

  useEffect(() => {
    setUploadProgress?.(progress);
  }, [progress]);

  useEffect(() => {
    if (cancelSending) {
      const errorMessage = 'Operation canceled by the user.';
      cancelAx(errorMessage);
    }
  }, [cancelSending]);

  const fileOnStart = (file: RcFile): Promise<RcFile> => {
    const fileSize =
      file.size > 1048576 ? Math.trunc(file.size / 1024 / 1024) : 1;
    return new Promise((resolve, reject) => {
      if (manualUpload) {
        if (fileSize > 10.01) {
          showToast({
            message: t('upload_limit'),
            type: 'info',
          });
        }

        setFile(file);
        setFileSize((file.size / 1024 / 1024).toFixed(2));
        onFileSelect?.(file);
        return false;
      }

      if (fileSize < 10.01) {
        resolve(file);
        setFile(file);
        setFileSize((file.size / 1024 / 1024).toFixed(2));
      } else {
        reject(new Error('Too big filesize'));
        showToast({
          message: t('upload_limit'),
          type: 'info',
        });
      }
    });
  };

  const fileOnSuccess = (response: GetRequestResponse<UploadFileResponse>) => {
    setProgress(null);
    setFile(null);
    setFileSize(null);
    setUploadProgress?.(null);
    callbackOnSuccess?.(response);
  };

  const onProgress = ({ percent }: { percent: number }) => {
    setProgress(Number(percent));
  };

  const onError = (err: unknown) => {
    console.log('err', err);
    setCancelSending?.(false);
    setProgress(null);
    setFile(null);
    setFileSize(null);
    setUploadProgress?.(null);
  };

  const action = envConfig.API_ROOT + endpoints.DOCUMENT_UPLOAD;

  return (
    <Upload
      accept={accept}
      beforeUpload={fileOnStart}
      className={cn('flex w-full', classNames)}
      customRequest={({ file, filename }) => {
        const formData = new FormData();
        if (typeof filename === 'string') {
          formData.append(filename, file);
        }

        //TODO: This should be set from outside by callback function
        if (meta) {
          formData.append('type', meta.type);
          formData.append('application_id', meta.applicationId);
          formData.append('company_tin', meta.companyTin);
        }

        axios
          .post<GetRequestResponse<UploadFileResponse>>(action, formData, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Device-Type': 'Web',
            },
            onUploadProgress: ({ total, loaded }) => {
              if (total) {
                onProgress({
                  percent: Number(
                    Math.round((loaded / total) * 100).toFixed(2),
                  ),
                });
              }
            },
            cancelToken: new CancelToken((c) => {
              cancelAx = c;
            }),
          })
          .then(({ data: response }) => {
            fileOnSuccess(response);
          })
          .catch((error) => {
            onError(error);
            if (
              axios.isAxiosError<PostRequestResponse, Record<string, unknown>>(
                error,
              )
            ) {
              const errorMessage = error?.response?.data?.message;
              if (typeof errorMessage === 'string') {
                showToast({
                  message: errorMessage || t('Что-то пошло не так'),
                  type: 'error',
                });
              }
            }
            showToast({
              message: t('Попробуйте снова загрузить файл'),
              type: 'error',
            });
          });
      }}
    >
      {children ? (
        children({ fileName, fileSize, setFile, setFileSize })
      ) : (
        <button
          type="button"
          className={cn(
            'text-grey-9da4ae bg-grey-f3f4f6 flex w-full items-center gap-x-2 rounded-lg p-3',
            classNames,
          )}
        >
          <IconDownloadFile className="flex-shrink-0" />
          <span className="font-base truncate text-base break-all">
            {fileName ? `${fileName}(${fileSize} mb)` : t(text)}
          </span>
        </button>
      )}
    </Upload>
  );
}
